<template>
  <BaseCard :color="bgClass" class="h-full">
    <component
      :is="clickable ? 'button' : 'div'"
      :type="clickable ? 'button' : undefined"
      class="w-full h-full text-left p-5 flex flex-col gap-2"
      :class="clickable ? 'transition-shadow hover:shadow-md cursor-pointer' : ''"
      @click="onClick"
    >
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-2 text-xs font-semibold" :class="labelClass">
          <Icon :name="icon" :size="16" :color="iconColor" />
          <span>{{ label }}</span>
        </div>
        <Icon v-if="clickable" name="mdi-arrow-top-right" :size="15" color="#3513e2" />
      </div>

      <template v-if="pending">
        <div class="text-3xl font-semibold leading-none tabular-nums text-slate-300">
          —
        </div>
        <div class="flex items-center gap-1 text-xs text-slate-400">
          <Icon name="mdi-clock-outline" :size="13" color="#94a3b8" />
          <span>{{ pendingLabel }}</span>
        </div>
      </template>
      <template v-else>
        <div class="text-3xl font-semibold leading-none tabular-nums" :class="valueClass">
          {{ displayValue }}
        </div>
        <div
          v-if="trend"
          class="flex items-center gap-1 text-xs font-semibold"
          :class="trend.direction === 'up' ? 'text-emerald-600' : 'text-red'"
        >
          <Icon
            :name="trend.direction === 'up' ? 'mdi-trending-up' : 'mdi-trending-down'"
            :size="14"
            :color="trend.direction === 'up' ? '#059669' : '#ff2525'"
          />
          <span>{{ trend.label }}</span>
        </div>
        <div v-else-if="subtitle" class="text-xs text-slate-500">
          {{ subtitle }}
        </div>
      </template>
    </component>
  </BaseCard>
</template>

<script setup lang="ts">
import {computed} from "vue";
import BaseCard from "@/core/components/base/BaseCard.vue";
import Icon from "@/core/components/icon/Icon.vue";

interface KpiTrend {
    direction: "up" | "down";
    label: string;
}

const props = withDefaults(defineProps<{
    /** The KPI label. */
    label: string;
    /** The KPI value (already formatted). Shown as "—" when nullish. */
    value?: string | number | null;
    /** The MDI icon name, e.g. "mdi-cash-multiple". */
    icon: string;
    /** Visual accent driving colors and background tint. */
    accent?: "brand" | "neutral" | "warning";
    /** Optional caption shown under the value when no trend is provided. */
    subtitle?: string | null;
    /** Optional evolution indicator shown under the value. */
    trend?: KpiTrend | null;
    /** When true, renders a muted "coming soon" state instead of the value. */
    pending?: boolean;
    /** Caption of the pending state ("Bientôt disponible" for unserved KPIs, "Chargement…" for in-flight ones). */
    pendingLabel?: string;
    /** When true, the tile becomes an actionable button. */
    clickable?: boolean;
}>(), {
    value: null,
    accent: "neutral",
    subtitle: null,
    trend: null,
    pending: false,
    pendingLabel: "Bientôt disponible",
    clickable: false
});

const emit = defineEmits<{(e: "click"): void}>();

const onClick = () => {
    if (props.clickable) {
        emit("click");
    }
};

const displayValue = computed(() => {
    if (props.value === null || props.value === undefined || props.value === "") {
        return "—";
    }

    return props.value;
});

const bgClass = computed(() => ({
    brand: "bg-primary-lightest",
    neutral: "bg-white",
    warning: "bg-amber-50"
}[props.accent]));

const labelClass = computed(() => ({
    brand: "text-primary",
    neutral: "text-slate-500",
    warning: "text-amber-700"
}[props.accent]));

const valueClass = computed(() => ({
    brand: "text-primary",
    neutral: "text-gray-dark",
    warning: "text-amber-700"
}[props.accent]));

const iconColor = computed(() => ({
    brand: "#3513e2",
    neutral: "#3513e2",
    warning: "#b45309"
}[props.accent]));
</script>
